import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { VotingType } from '../../../common/core-api';
import { useRuntimeUser } from '../../../context';
import { type VotingOptionsData } from '../IdeaCard.types';
import { UpDownVote } from './UpDownVote';
import { StarVote } from './StarVote';
import { PointsAwardVote } from './PointsAwardVote';

interface VoteActionsProps {
  readonly ideaId: string;
  readonly viewerVoteDirection: number | null;
  readonly votingType: VotingType | null | undefined;
  readonly votingOptions?: VotingOptionsData | null;
  readonly disabled?: boolean;
  readonly onVoteSuccess?: (result: { voteCount: number; score?: number; hasReachedFinalMilestone?: boolean }) => void;
  readonly onVoteError?: (message: string) => void;
}

export const VoteActions = ({
  ideaId,
  viewerVoteDirection,
  votingType,
  votingOptions,
  disabled = false,
  onVoteSuccess,
  onVoteError
}: VoteActionsProps) => {
  const [localVote, setLocalVote] = useState<number | null>(viewerVoteDirection);
  const [loading, setLoading] = useState(false);
  const { actionKey } = useRuntimeUser();
  const intl = useIntl();

  useEffect(() => {
    setLocalVote(viewerVoteDirection);
  }, [viewerVoteDirection]);

  const handleVote = useCallback(
    async (direction: number) => {
      if (loading || disabled) return;

      const previousVote = localVote;
      const isToggle = previousVote === direction;
      setLocalVote(isToggle ? null : direction);
      setLoading(true);

      try {
        const response = await fetch(`/post/vote/${ideaId}?vote_direction=${direction}&ak=${actionKey}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const data = (await response.json()) as {
          success: number;
          message?: string;
          voteCount?: number;
          score?: number;
          viewerVoteDirection?: number;
        };

        if (!data.success) {
          setLocalVote(previousVote);
          const errorText =
            data.message?.replace(/<[^>]*>/g, ' ').trim() ||
            intl.formatMessage({ id: 'voteFailed', defaultMessage: 'Vote failed' });
          onVoteError?.(errorText);
          return;
        }

        setLocalVote(data.viewerVoteDirection ?? null);
        onVoteSuccess?.({
          voteCount: data.voteCount ?? 0,
          score: data.score ?? 0,
          hasReachedFinalMilestone: false
        });
      } catch {
        setLocalVote(previousVote);
        onVoteError?.(intl.formatMessage({ id: 'voteFailedRetry', defaultMessage: 'Vote failed. Please try again.' }));
      } finally {
        setLoading(false);
      }
    },
    [ideaId, localVote, loading, disabled, actionKey, intl, onVoteSuccess, onVoteError]
  );

  if (!votingType || votingType === VotingType.SCORECARD) {
    return null;
  }

  const isDisabled = disabled || loading;

  let voteControl: React.ReactNode = null;

  switch (votingType) {
    case VotingType.UP:
    case VotingType.UPDOWN: {
      voteControl = (
        <UpDownVote votingType={votingType} localVote={localVote} disabled={isDisabled} onVote={handleVote} />
      );

      break;
    }
    case VotingType.STARS: {
      const maxStars = votingOptions?.maxStars ?? 3;
      voteControl = <StarVote maxStars={maxStars} localVote={localVote} disabled={isDisabled} onVote={handleVote} />;

      break;
    }
    case VotingType.POINTSAWARD: {
      const maxScore = votingOptions?.maxScore ?? 1;
      voteControl = (
        <PointsAwardVote maxScore={maxScore} localVote={localVote} disabled={isDisabled} onVote={handleVote} />
      );

      break;
    }
    default:
      break;
  }

  return voteControl;
};
